import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import useCanonicalUrl from '../hooks/useCanonicalUrl'
import useFAQSchema from '../hooks/useFAQSchema'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import Breadcrumb from '../components/Breadcrumb'
import {
    setActiveTab,
    setExamBirthDate, setExamType, calculateExamEligibility, clearExamEligibility,
    setEligBirthDate, calculateEligibility, clearEligibility,
} from '../store/slices/ageSlice'
import '../pages/AgePage.css'

const EligibilityCalculatorPage = ({ tools = [] }) => {
    useCanonicalUrl()
    useFAQSchema()
    const dispatch = useDispatch()
    const {
        activeTab,
        examBirthDate, examType, examResult,
        eligBirthDate, eligResult,
    } = useSelector(state => state.age)
    const [lastUpdated, setLastUpdated] = useState(null)

    useEffect(() => {
        setLastUpdated(
            new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        )
    }, [])

    useEffect(() => {
        dispatch(setActiveTab('exam'))
    }, [dispatch])

    useEffect(() => {
        document.title = 'Age Eligibility Calculator - Exam & Milestone Age Checker | EveryKits'
        const metaDescription = document.querySelector('meta[name="description"]')
        if (metaDescription) {
            metaDescription.setAttribute('content', 'Check age eligibility for competitive exams (CSS, PPSC, FPSC) and life milestones like voting, driving, and retirement.')
        }
        const structuredData = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Age Eligibility Calculator",
            "applicationCategory": "WebApplication",
            "operatingSystem": "All",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "description": "Free age eligibility calculator for exams and life milestones.",
            "url": "https://everykits.com/eligibility-calculator/"
        }
        document.querySelectorAll('script[data-schema="software-application"]').forEach(el => el.remove());
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-schema', 'software-application');
        script.textContent = JSON.stringify(structuredData)
        document.head.appendChild(script)
        return () => {
            if (document.head.contains(script)) {
                document.head.removeChild(script);
            }
        };
    }, [])

    return (
        <ToolPageTemplate
            title="Age Eligibility Calculator"
            description="Check your eligibility for exams and life milestones based on age"
            tools={tools}
            breadcrumbItems={[
                { name: 'Home', path: '/' },
                { name: 'Age Calculator', path: '/age/' },
                { name: 'Eligibility Calculator', path: '/eligibility-calculator/' },
            ]}
        >
            <div>
                <div className="age-tabs">
                    <button className={`age-tab ${activeTab === 'exam' ? 'active' : ''}`} onClick={() => dispatch(setActiveTab('exam'))}>Exam Eligibility</button>
                    <button className={`age-tab ${activeTab === 'eligibility' ? 'active' : ''}`} onClick={() => dispatch(setActiveTab('eligibility'))}>General Milestones</button>
                </div>

                {activeTab === 'exam' && (
                    <section className="age-calculator">
                        <div className="age-input-section">
                            <div className="age-item" style={{ marginBottom: '12px' }}>
                                <span className="age-label">Exam Type:</span>
                                <select value={examType} onChange={(e) => dispatch(setExamType(e.target.value))}>
                                    <option value="css">CSS</option>
                                    <option value="ppsc">PPSC</option>
                                    <option value="fpsc">FPSC</option>
                                </select>
                            </div>
                            <Input type="date" label="Date of Birth" value={examBirthDate} onChange={(e) => dispatch(setExamBirthDate(e.target.value))} max={new Date().toISOString().split('T')[0]} />
                            <div className="age-buttons">
                                <Button onClick={() => dispatch(calculateExamEligibility())} disabled={!examBirthDate}>Check Eligibility</Button>
                                <Button variant="secondary" onClick={() => dispatch(clearExamEligibility())}>Clear</Button>
                            </div>
                        </div>
                        {examResult && (
                            <ResultBox title={`${examResult.label} Age Eligibility`}>
                                <div className="age-details">
                                    <div className="age-item"><span className="age-label">Your Current Age:</span><span className="age-value">{examResult.age}</span></div>
                                    <div className="age-item"><span className="age-label">Eligible Age Range:</span><span className="age-value">{examResult.min} - {examResult.max}</span></div>
                                    <div className="age-item"><span className="age-label">Status:</span><span className="age-value">{examResult.eligible ? 'Eligible ✓' : 'Not Eligible ✗'}</span></div>
                                </div>
                            </ResultBox>
                        )}
                    </section>
                )}

                {activeTab === 'eligibility' && (
                    <section className="age-calculator">
                        <div className="age-input-section">
                            <Input type="date" label="Date of Birth" value={eligBirthDate} onChange={(e) => dispatch(setEligBirthDate(e.target.value))} max={new Date().toISOString().split('T')[0]} />
                            <div className="age-buttons">
                                <Button onClick={() => dispatch(calculateEligibility())} disabled={!eligBirthDate}>Check Eligibility</Button>
                                <Button variant="secondary" onClick={() => dispatch(clearEligibility())}>Clear</Button>
                            </div>
                        </div>
                        {eligResult && (
                            <ResultBox title={`Eligibility at Age ${eligResult.age}`}>
                                <div className="age-details">
                                    {eligResult.milestones.map((ms, i) => (
                                        <div className="age-item" key={i}>
                                            <span className="age-label">{ms.label}:</span>
                                            <span className="age-value">{ms.eligible ? 'Eligible ✓' : `${ms.yearsLeft} yrs left`}</span>
                                        </div>
                                    ))}
                                </div>
                            </ResultBox>
                        )}
                    </section>
                )}

                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>How to Use Eligibility Calculator</h2>
                        <div className="guide-steps">
                            <div className="step">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <h3>Choose a Category</h3>
                                    <p>Switch between "Exam Eligibility" for competitive exams or "General Milestones" for life event eligibility.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <h3>Enter Date of Birth</h3>
                                    <p>Provide your date of birth to calculate your current age accurately.</p>
                                </div>
                            </div>
                            <div className="step">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <h3>Review Eligibility Status</h3>
                                    <p>Instantly see whether you meet the age requirement, and how many years remain until you become eligible or age out.</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="guide-section">
                        <h2>Why Use This Tool</h2>
                        <p>Competitive exams like CSS, PPSC, and FPSC have strict age brackets, and missing them by even a few months can disqualify an application. This tool instantly checks your eligibility against official age ranges, plus offers a general milestone checker covering common life events like voting, driving, and retirement eligibility — all in one place.</p>
                    </div>
                    <div className="guide-section">
                        <h2>Frequently Asked Questions</h2>
                        <div className="faq-list">
                            <div className="faq-item">
                                <h3>What is the age limit for CSS exam?</h3>
                                <p>The general CSS age limit is 21 to 30 years, though relaxations may apply for certain categories—always verify with the official FPSC notification.</p>
                            </div>
                            <div className="faq-item">
                                <h3>Can I check eligibility for multiple exams?</h3>
                                <p>Yes, switch between CSS, PPSC, and FPSC using the dropdown to check your eligibility for each exam type.</p>
                            </div>
                            <div className="faq-item">
                                <h3>What milestones does the general checker cover?</h3>
                                <p>It covers common life milestones like voting age, driving age, employment eligibility, and retirement age benchmarks.</p>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="tool-guide">
                    <div className="guide-section">
                        <h2>Related Tools</h2>
                        <div className="related-tools">
                            <Link to="/age/" className="related-tool">
                                <div className="tool-icon">Age Calculator</div>
                                <div className="tool-description">Calculate your exact age and age difference between dates</div>
                            </Link>
                            <Link to="/retirement-age-calculator/" className="related-tool">
                                <div className="tool-icon">Retirement Age Calculator</div>
                                <div className="tool-description">Find out your estimated retirement date</div>
                            </Link>
                            <Link to="/chronological-age-calculator/" className="related-tool">
                                <div className="tool-icon">Chronological Age Calculator</div>
                                <div className="tool-description">Precise age format used for testing and assessment eligibility</div>
                            </Link>
                            <Link to="/islamic-age-calculator/" className="related-tool">
                                <div className="tool-icon">Islamic Age Calculator</div>
                                <div className="tool-description">Check your age in the Hijri calendar alongside eligibility milestones</div>
                            </Link>
                            <Link to="/percentage/" className="related-tool">
                                <div className="tool-icon">Percentage Calculator</div>
                                <div className="tool-description">Calculate exam scores and percentage-based results</div>
                            </Link>
                        </div>
                    </div>
                </section>

                <div className="last-updated">
                    <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
                </div>
            </div>
        </ToolPageTemplate>
    )
}

export default EligibilityCalculatorPage